import { getPinComponentFragment } from '@lessonup/editor-shared';
import { TipTapEditorContent, useKeybindController, useTipTapEditor } from '@lessonup/pin-renderer';
import { PinComponentId, PinId, tipTapBaseExtensions, tipTapLeanExtensions } from '@lessonup/pins-shared';
import { inputFieldPlaceholderOpacity, styled } from '@lessonup/ui-components';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Collaboration } from '@tiptap/extension-collaboration';
import { compact, debounce, isNull } from 'lodash';
import React, { CSSProperties, forwardRef, useMemo } from 'react';
import { XmlFragment as YXmlFragment } from 'yjs';
import { teacherTextEditorStore } from './EditorYjs/teacherTextEditorStore';
import { useAnyYjs } from './YjsContext';
import { NewLineDisabler } from './extensions/NewLineDisabler';
import { PasteHandler } from './extensions/PasteHandler';
import { Placeholder } from './extensions/Placeholder';

interface CSSPropertiesWithVars extends CSSProperties {
  '--component-placeholder'?: string;
}

interface TiptapInputProps {
  pinId: PinId;
  pinComponentId: PinComponentId;
  fragmentKey: string;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  disableNewLine?: boolean;
  useBaseExtensions?: boolean;
  maxInputLength?: number;
}

export const TipTapInput = forwardRef<HTMLDivElement, TiptapInputProps>((props, ref) => {
  const { pinId, pinComponentId, fragmentKey, disableNewLine, placeholder } = props;
  const { yDoc } = useAnyYjs();
  const fragment = useMemo(
    () => getPinComponentFragment(yDoc, pinId, pinComponentId, fragmentKey),
    [yDoc, pinId, pinComponentId, fragmentKey]
  );

  if (isNull(fragment)) return null;

  return (
    <StyledTipTapInputWrapper ref={ref} style={computeTipTapInputStyles(placeholder)}>
      <TipTapInputInner fragment={fragment} {...props} disableNewLine={!!disableNewLine} />
    </StyledTipTapInputWrapper>
  );
});

TipTapInput.displayName = 'TipTapInput';

const StyledTipTapInputWrapper = styled.div`
  .ProseMirror:focus {
    outline: none;
  }
  .ProseMirror {
    width: 100%;
  }
  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: var(--component-placeholder);
    pointer-events: none;
    float: left;
    height: 0;
    overflow-wrap: anywhere;
    opacity: ${inputFieldPlaceholderOpacity};
  }
`;

interface TipTapComponentInnerProps extends TiptapInputProps {
  fragment: YXmlFragment;
}

export const TipTapInputInner: React.FC<TipTapComponentInnerProps> = ({
  pinId,
  pinComponentId,
  onBlur,
  onFocus,
  fragment,
  disableNewLine,
  useBaseExtensions,
  maxInputLength,
}) => {
  const { yDoc, collaborationCursor } = useAnyYjs();
  const keybindController = useKeybindController();
  const debouncedUpdate = debounce(() => {
    teacherTextEditorStore.forceEditorUpdate();
  }, 100);

  const editor = useTipTapEditor(
    {
      immediatelyRender: false,
      editable: true,
      onBlur: () => {
        onBlur?.();
        keybindController.enableKeybinds();
      },
      onFocus: () => {
        onFocus?.();
        keybindController.disableKeybinds();
      },
      onTransaction: () => {
        debouncedUpdate();
      },
      extensions: compact([
        ...(useBaseExtensions ? tipTapBaseExtensions : tipTapLeanExtensions),
        PasteHandler,
        disableNewLine ? NewLineDisabler : null,
        Collaboration.extend({
          // We have to override undo and redo from tiptap, since we already have the undo manager directly integrated in our editor
          addCommands() {
            return {
              undo: () => () => {
                return false;
              },
              redo: () => () => {
                return false;
              },
            };
          },
        }).configure({
          document: yDoc,
          fragment: fragment,
        }),
        collaborationCursor,
        Placeholder,
        maxInputLength
          ? CharacterCount.configure({
              limit: maxInputLength,
            })
          : null,
      ]),
    },
    [pinId, pinComponentId, useBaseExtensions]
  );

  return <TipTapEditorContent className="tiptap-root" editor={editor} />;
};

function computeTipTapInputStyles(placeholder?: string): CSSPropertiesWithVars {
  return {
    ...(placeholder ? { '--component-placeholder': `"${placeholder}"` } : {}),
  };
}
